import { MultipleOfferAvailability } from '../../../../types/MultipleOfferAvailability';
import { Button } from '../../../cdl/Button/Button';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { IconPlus } from '../../../common/icons/cdl/Plus';
import { Box } from '../../../common/ui/Box';
import { OfferModel } from '../../../offer/model/OfferModel';
import { getOpenRfqOffers } from '../helpers/getOpenRfqOffers';

import { AdditionalOfferActionButton } from './AdditionalOfferActionButton';
import { RfqOfferList } from './RfqOfferList';

interface RfqOfferListSectionProps {
    offers: OfferModel[];
    supplierId: string;
    multipleOfferAvailability: MultipleOfferAvailability;
    isEnquiryState: boolean;
    onCreateQuote: () => void;
}

export const RfqOfferListSection = ({
    offers,
    supplierId,
    multipleOfferAvailability,
    isEnquiryState,
    onCreateQuote,
}: RfqOfferListSectionProps) => {
    const role = useRole();
    const canCreateAdditionalOffer = multipleOfferAvailability === MultipleOfferAvailability.ENABLED;
    const isDisabledForCustomer = multipleOfferAvailability === MultipleOfferAvailability.DISABLED_FOR_CUSTOMER;
    const openOffers = getOpenRfqOffers(offers);
    const hasQuotedOffer = openOffers.some((offer) => offer.wasQuoted());
    const canCreateQuote =
        role.isSupplier() &&
        role.hasWriteRights(supplierId) &&
        openOffers.length > 0 &&
        !hasQuotedOffer &&
        isEnquiryState;
    const showPluralOfferTitle = canCreateAdditionalOffer || offers.length > 1;
    const showAdditionalOfferAction =
        role.isSupplier() &&
        role.hasWriteRights(supplierId) &&
        openOffers.length > 0 &&
        hasQuotedOffer &&
        isEnquiryState &&
        (canCreateAdditionalOffer || isDisabledForCustomer);

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={16}>
                <Text variant="title" as="h2">
                    {translate(showPluralOfferTitle ? 'rfq.detail.offers' : 'rfq.detail.offer')}
                </Text>
                {canCreateQuote ? (
                    <Button LeadingIcon={IconPlus} onClick={onCreateQuote}>
                        {translate('statusbar.supplier.enquiry.button')}
                    </Button>
                ) : null}
                {showAdditionalOfferAction ? (
                    <AdditionalOfferActionButton
                        disabledReason={
                            isDisabledForCustomer
                                ? translate('rfq.detail.additionalOfferUnavailableForCustomer')
                                : undefined
                        }
                        onClick={onCreateQuote}
                    />
                ) : null}
            </Box>
            <RfqOfferList offers={offers} isMultipleOffersEnabled={canCreateAdditionalOffer} />
        </Box>
    );
};
